import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Shield, AlertTriangle, Phone, Mail } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <article className="bg-gray-800 rounded-2xl p-8 md:p-12 border border-gray-700">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Jeu Responsable
              </h1>
              <p className="text-gray-400 text-sm mb-8">
                Dernière mise à jour : 26 novembre 2025
              </p>
              
              <div className="prose prose-invert max-w-none">
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Chez Galo Bet, nous sommes profondément engagés dans la promotion du jeu responsable. Nous comprenons que le paris en ligne doit rester une activité de divertissement et non devenir un problème. Cette page fournit des informations essentielles, des ressources d'aide, et des outils pour vous aider à maintenir un contrôle total sur votre activité de parieur.
                </p>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Heart className="text-pink-400" size={28} />
                    <span>Notre Engagement envers le Jeu Responsable</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Galo Bet s'engage à promouvoir le jeu responsable et à protéger les parieurs vulnérables. Nous croyons fermement que le paris en ligne doit être une activité agréable et contrôlée, jamais une source de problèmes financiers, personnels ou sociaux.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous ne ciblons jamais les mineurs et n'encourageons jamais les comportements de jeu excessifs. Tous nos contenus sont destinés aux personnes majeures (18 ans et plus en France) et mettent en avant l'importance de la modération et du contrôle.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous encourageons tous les parieurs à établir des limites claires, à ne jamais parier plus que ce qu'ils peuvent se permettre de perdre, et à chercher de l'aide dès les premiers signes de problème.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <AlertTriangle className="text-pink-400" size={28} />
                    <span>Signes d'Alerte du Jeu Excessif</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Il est important de reconnaître les signes précoces d'un comportement de jeu problématique. Si vous ou quelqu'un que vous connaissez présentez ces signes, il est temps de demander de l'aide :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li>Parier plus que ce que vous pouvez vous permettre de perdre</li>
                    <li>Essayer de "rattraper" les pertes en augmentant les mises</li>
                    <li>Mentir à votre famille ou à vos amis sur vos activités de paris</li>
                    <li>Négliger vos responsabilités professionnelles, familiales ou personnelles à cause des paris</li>
                    <li>Emprunter de l'argent ou vendre des biens pour financer vos paris</li>
                    <li>Sentir de l'anxiété, du stress ou de la dépression liés aux paris</li>
                    <li>Penser constamment aux paris, même quand vous ne pariez pas</li>
                    <li>Ne plus pouvoir contrôler le temps ou l'argent consacrés aux paris</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Si plusieurs de ces signes vous concernent, il est important de prendre des mesures immédiates. Le problème ne disparaîtra pas de lui-même et nécessite une action proactive.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Shield className="text-pink-400" size={28} />
                    <span>Outils de Jeu Responsable</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Les sites de paris agréés en France offrent de nombreux outils pour vous aider à maintenir le contrôle. Nous vous encourageons vivement à les utiliser :
                  </p>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <h3 className="text-white font-semibold mb-3">Limites de Dépôt</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Définissez un montant maximum que vous pouvez déposer sur une période donnée (jour, semaine, mois). Une fois cette limite atteinte, vous ne pourrez pas déposer davantage jusqu'à la période suivante.
                    </p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <h3 className="text-white font-semibold mb-3">Limites de Perte</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Fixez un montant maximum que vous êtes prêt à perdre. Quand cette limite est atteinte, votre compte est temporairement verrouillé pour les dépôts, vous forçant à faire une pause et à réévaluer.
                    </p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <h3 className="text-white font-semibold mb-3">Limites de Temps</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Définissez une durée maximale que vous pouvez passer à parier. Des alertes vous rappelleront de faire des pauses régulières.
                    </p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <h3 className="text-white font-semibold mb-3">Auto-Exclusion</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Si vous sentez que vous perdez le contrôle, vous pouvez demander une auto-exclusion temporaire ou permanente. Pendant cette période, vous ne pourrez pas accéder à votre compte, même si vous changez d'avis.
                    </p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <h3 className="text-white font-semibold mb-3">Rappels et Alertes</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Configurez des rappels réguliers qui vous indiquent combien de temps vous avez passé à parier et combien vous avez dépensé. Ces alertes vous aident à maintenir une perspective claire sur votre activité.
                    </p>
                  </div>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">Ressources d'Aide en France</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Si vous avez besoin d'aide, de nombreuses ressources gratuites et confidentielles sont disponibles en France :
                  </p>
                  <div className="bg-pink-600/10 border border-pink-600/30 rounded-lg p-6 mb-4">
                    <h3 className="text-pink-300 font-semibold mb-3 flex items-center space-x-2">
                      <Phone className="text-pink-400" size={20} />
                      <span>Joueurs Info Service</span>
                    </h3>
                    <p className="text-gray-300 text-sm mb-2">
                      Service d'écoute et d'orientation gratuit et confidentiel, 7j/7, de 8h à 2h.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Tel :</strong> <a href="tel:0974751313" className="text-pink-400 hover:text-pink-300">09 74 75 13 13</a>
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Site :</strong> <a href="https://www.joueurs-info-service.fr/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">www.joueurs-info-service.fr</a>
                    </p>
                  </div>
                  <div className="bg-pink-600/10 border border-pink-600/30 rounded-lg p-6 mb-4">
                    <h3 className="text-pink-300 font-semibold mb-3">SOS Joueurs</h3>
                    <p className="text-gray-300 text-sm mb-2">
                      Association d'aide aux joueurs en difficulté et à leurs proches.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Site :</strong> <a href="https://www.sosjoueurs.org/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">www.sosjoueurs.org</a>
                    </p>
                  </div>
                  <div className="bg-pink-600/10 border border-pink-600/30 rounded-lg p-6 mb-4">
                    <h3 className="text-pink-300 font-semibold mb-3">EvaluJeu</h3>
                    <p className="text-gray-300 text-sm mb-2">
                      Outil d'auto-évaluation pour évaluer votre relation avec le jeu.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Site :</strong> <a href="https://www.evalujeu.fr/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">www.evalujeu.fr</a>
                    </p>
                  </div>
                  <div className="bg-pink-600/10 border border-pink-600/30 rounded-lg p-6 mb-4">
                    <h3 className="text-pink-300 font-semibold mb-3">Interdiction Volontaire</h3>
                    <p className="text-gray-300 text-sm mb-2">
                      Vous pouvez demander une interdiction de jeu auprès de l'Autorité Nationale des Jeux (ANJ). Cette interdiction s'applique à tous les opérateurs agréés en France pour une durée minimale de 3 ans.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Site ANJ :</strong> <a href="https://www.anj.fr/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">www.anj.fr</a>
                    </p>
                  </div>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">Conseils pour Parier de Manière Responsable</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Voici quelques principes fondamentaux pour maintenir une relation saine avec les paris en ligne :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li><strong>Ne jamais parier plus que ce que vous pouvez vous permettre de perdre</strong> - Considérez les paris comme un divertissement, pas comme un investissement.</li>
                    <li><strong>Fixez des limites avant de commencer</strong> - Décidez à l'avance combien d'argent et de temps vous allez consacrer aux paris, et respectez ces limites.</li>
                    <li><strong>Ne jamais "chasser" les pertes</strong> - Acceptez les pertes comme partie normale de l'activité et ne tentez pas de les récupérer en augmentant les mises.</li>
                    <li><strong>Ne pariez jamais sous l'influence de l'alcool ou des émotions fortes</strong> - Les décisions impulsives mènent souvent à des pertes importantes.</li>
                    <li><strong>Prenez des pauses régulières</strong> - Ne laissez pas les paris devenir une activité quotidienne obsessionnelle.</li>
                    <li><strong>Ne pariez jamais pour résoudre des problèmes financiers</strong> - Les paris peuvent aggraver une situation financière difficile.</li>
                    <li><strong>Gardez une trace de vos paris</strong> - Suivez vos dépenses et vos gains pour maintenir une perspective claire sur votre activité.</li>
                  </ul>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">Protection des Mineurs</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Le paris en ligne est strictement réservé aux personnes majeures (18 ans et plus en France). Tous les sites de paris agréés en France sont tenus de vérifier l'âge et l'identité de leurs clients avant d'autoriser toute activité de paris.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Si vous êtes parent ou tuteur et que vous soupçonnez qu'un mineur accède à des sites de paris, prenez des mesures immédiates : installez des logiciels de contrôle parental, surveillez l'activité en ligne, et éduquez sur les risques du jeu pour les mineurs.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">Notre Engagement Continu</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Galo Bet continuera à promouvoir activement le jeu responsable à travers :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li>L'inclusion d'informations sur le jeu responsable dans tous nos contenus</li>
                    <li>La mise en avant des outils de jeu responsable disponibles sur les sites</li>
                    <li>La promotion des ressources d'aide et de soutien</li>
                    <li>L'éducation sur les risques et les signes d'alerte</li>
                  </ul>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">Contact</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Si vous avez des questions concernant le jeu responsable ou si vous souhaitez signaler un problème, n'hésitez pas à nous contacter :
                  </p>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <p className="text-gray-300 mb-2"><strong>Email :</strong> <a href="mailto:info@galo-bet.com" className="text-pink-400 hover:text-pink-300">info@galo-bet.com</a></p>
                    <p className="text-gray-300 mb-2"><strong>Téléphone :</strong> <a href="tel:+33519286219" className="text-pink-400 hover:text-pink-300">+33 5 19 28 62 19</a></p>
                    <p className="text-gray-300"><strong>Adresse :</strong> Paris, France</p>
                  </div>
                </section>

                <div className="bg-pink-600/20 border border-pink-600/50 rounded-xl p-8 mt-10">
                  <p className="text-white font-semibold text-lg text-center mb-4">
                    Le jeu doit rester un plaisir. Si ce n'est plus le cas, demandez de l'aide.
                  </p>
                  <p className="text-gray-300 text-center">
                    <strong>Joueurs Info Service :</strong> <a href="tel:0974751313" className="text-pink-400 hover:text-pink-300 font-semibold">09 74 75 13 13</a> (appel gratuit)
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

